import { router } from "@inertiajs/react";
import { Plus, Send, Upload } from "lucide-react";
import { useRef, useState } from "react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";
import { OutboundList, STATUS_COLORS, STATUS_LABELS, csrfToken, fmt } from "./_types";

interface Props {
  lists: OutboundList[];
}

export default function OutboundIndex({ lists }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState("");

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);

    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name.trim() || file.name.replace(/\.[^.]+$/, ""));
    fd.append("authenticity_token", csrfToken());

    fetch("/outbound/lists", { method: "POST", body: fd })
      .then((res) => { if (res.redirected) window.location.href = res.url; else router.reload(); })
      .finally(() => { setUploading(false); setName(""); });
  }

  return (
    <AppLayout title="Outbound">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Outbound</h1>
          <p className="text-sm text-gray-500 mt-0.5">Upload Leopard exports (CSV or XLSX), run DNC checks, send blasts</p>
        </div>
      </div>

      {/* Upload zone */}
      <div
        className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center mb-8 hover:border-green-400 hover:bg-green-50/20 transition-colors cursor-pointer"
        onClick={() => inputRef.current?.click()}
      >
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFile} />
        <Upload className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-600 mb-1">Drop a Leopard export here</p>
        <p className="text-xs text-gray-400 mb-4">CSV or XLSX · Max 5 MB</p>
        <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="List name (optional)"
            className="text-sm border border-gray-200 rounded px-3 py-1.5 w-48 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
            <Plus className="w-3.5 h-3.5 mr-1" /> {uploading ? "Uploading..." : "Upload List"}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {lists.length === 0 ? (
          <div className="py-16 text-center">
            <Send className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No outbound lists yet. Upload a CSV or XLSX to get started.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Clean</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Blocked</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Uploaded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lists.map((l) => (
                <tr key={l.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => router.visit(`/outbound/lists/${l.id}`)}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{l.name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[l.status] ?? "bg-gray-100 text-gray-500"}`}>
                      {STATUS_LABELS[l.status] ?? l.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{l.total_count}</td>
                  <td className="px-4 py-3 text-sm text-green-600">{l.clean_count || "—"}</td>
                  <td className="px-4 py-3 text-sm text-red-500">{l.blocked_count || "—"}</td>
                  <td className="px-4 py-3 text-right text-xs text-gray-400">{fmt(l.uploaded_at ?? l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}
